import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

// Style and SEO
import { Col, Grid, Row, styled, up, css, th, thd } from '@smooth-ui/core-sc';
import Helmet from 'react-helmet';
import IconWrapper from '_platform/src/components/SvgIcon/IconWrapper';
import SvgIcon from '../../components/SvgIcon/SvgIcon';

import { withSettings } from '../../containers/WebApp/SettingsContext';
import PrivateComponent from '_platform/src/utils/PrivateComponent';
import LoadAsync from '_platform/src/utils/LoadAsync';
import LoadingAnimation from '_platform/src/components/Loading/LoadingAnimation';
import RexelBanners from '../../components/CustomComponents/Banners/RexelBanners';

import cardsTop from './images/home-rewards-top.png';
import cardsBottom from './images/home-rewards-bottom.png';

import Clipsal from '../../theme/630x155-FeaturedVendor-Logo-@2x.png';
import Atom from '../../components/CustomComponents/CustomFooter/images/FooterLogo-Atom@2x.png';
import Trader from '../../components/CustomComponents/CustomFooter/images/FooterLogo-Trader@2x.png';

import AdvantageLogo from 'theme/Logo-2026.png';

import BgWhite from 'components/CustomComponents/Backgrounds/BgWhite';

const StatementProvider = LoadAsync(() =>
  import(
    /* webpackChunkName: "statementProvider" */ '../StatementProvider/StatementProvider'
  )
);

const CustomStatementSummary = LoadAsync(() =>
  import(
    /* webpackChunkName: "customStatementSummary" */ '../StatementPage/CustomStatementSummary'
  )
);

const RewardsProvider = LoadAsync(() =>
  import(
    /* webpackChunkName: "rewardsProvider" */ '../RewardsProvider/RewardsProvider'
  )
);

const PageContainer = styled.div`
  .banner__title {
    letter-spacing: 0;
  }

  h1 {
    font-weight: normal;
  }

  .h1 {
    margin-top: 0;
  }

  h2.heading--how {
    margin-bottom: 0;
  }
`;

const StyledLogo = styled.img`
  height: auto;
  margin: 0 auto;
  width: 100%;
  display: block;
  max-width: 700px;
`;

const FeaturedSupplierContainer = styled.div`
  color: ${th('primary')};
  background: white;

  h3 {
    margin-top: 15px;
    margin-bottom: 0;
  }

  img {
    max-height: 80px;
    width: auto;
  }

  .divider {
    padding-bottom: 10px;
    border-bottom: 1px solid #000;
  }
`;

const IntroContainer = styled.div`
  align-items: center;
  background-position: center top;
  background-size: 100% auto;
  background-repeat: no-repeat;
  color: #fff;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  justify-content: center;
`;

const HomeBgGradient = styled.div`
  background-image: linear-gradient(45deg, #c42f35 0, #1550a0 100%);
`;

/*const MegaDrawContainer = styled.div`
  position: relative;
  .megadraw-logo {
    max-width: 320px;
    margin-bottom: -10px;

    ${up(
      'lg',
      css`
        max-width: 370px;
        position: absolute;
        left: -100px;
        margin-bottom: auto;
        margin-top: -200px;
        top: 50%;
      `
    )};
  }

  .megadraw-text-container {
    background-color: #901a1c;
    border-radius: 48px;
    padding: 1em 2em;
    margin-top: -50px;

    ${up(
      'lg',
      css`
        margin-top: 0;
        padding-left: 15em;
      `
    )};

    h2 {
      margin: 0;
    }

    .timeline-container {
      position: relative;
      margin: 10px 0;

      .timeline {
        display: flex;
        justify-content: space-between;
        font-weight: bold;
        position: relative;

        .circle {
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 20px;
          background-color: white;
          color: #901a1c;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          margin: 0 auto;
        }
      }

      .line {
        width: 84%;
        height: 10px;
        background-color: white;
        position: absolute;
        top: 20px;
        left: 8%;
      }
    }

    p {
      margin: 0;
    }
  }
`;*/

const GiftCardContainer = styled.div`
  align-items: center;
  background-image: linear-gradient(45deg, #ac385b 0, #1550a0 100%);
  color: #fff;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  justify-content: center;
  padding: 80px 0;
  position: relative;

  .card-top {
    position: absolute;
    top: 0;
    left: 0;
  }

  .card-bottom {
    position: absolute;
    bottom: 0;
    right: 0;
  }

  .highlight {
    padding-bottom: 10px;
    font-size: 24px;
    font-weight: bold;
  }

  .text--primary {
    color: ${th('primary')};
  }
`;

const InfoSection = styled.section`
  align-items: center;
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0;
  width: 100%;

  .spacer--around--small {
    padding-right: 0;
    ${up(
      'md',
      css`
        padding-right: 20px;
      `
    )};

    ${up(
      'lg',
      css`
        padding-right: 20px;
      `
    )};
  }

  .how--it--works {
    border-top: 1px solid #fff;
  }

  .promo--dates {
    margin: 40px 0;
    padding: 40px 0;
    border-top: 1px solid #fff;
    border-bottom: 1px solid #fff;
    font-size: 24px;
  }

  .text--primary {
    color: ${th('primary')};
  }
`;

const StatementStyles = styled.div`
  padding: 10px;
  padding-bottom: 30px;
  width: 100%;
  max-width: 800px;

  section:not(:last-of-type) {
    padding-bottom: 1.25rem;
  }

  h2,
  .points-value span,
  .statement-summary-label,
  .statement-dollars-label,
  .statement-dollars-label span,
  .statement-dollars-redeemed-label,
  .statement-dollars-redeemed-label span {
    color: #fff;
  }

  .statement-summary {
    min-height: 450px;

    ${up(
      'md',
      css`
        min-height: 300px;
      `
    )};
  }

  span.points-value span {
    color: #fff;
  }

  .statement-summary-label {
    span.highlight-primary {
      color: #fff;
      display: block;
    }
  }

  .statement-summary-plus {
    margin: 30px 0;

    ${up(
      'md',
      css`
        margin-top: 210px;
      `
    )};
  }

  .statement-entries-plus {
    margin: 30px 0;

    ${up(
      'md',
      css`
        margin-top: 90px;
      `
    )};
  }

  .fine-print {
    color: ${th('primary')};
    font-weight: bold;
    margin-top: 25px;
  }

  .statement-dollars-label {
    font-size: 30px;
    &:before {
      content: '$';
    }
  }

  .statement-dollars-redeemed-label {
    font-size: 30px;
    &:before {
      content: '($';
    }
    &:after {
      content: ')';
    }
  }

  p.button--wrapper {
    text-align: center;
    a {
      text-decoration: none;
      color: ${thd('primary', '#0154C4')};
      font-weight: bold;
      background-color: white;
      border: 1px solid white;
      padding: 10px 50px 10px 50px;
      &:hover,
      &:focus {
        color: white;
        background-color: ${thd('primary', '#0154C4')};
      }
    }
  }
`;

const HomePage = ({ settings }) => {
  return (
    <PageContainer>
      <Helmet>
        {/* <meta name="description" content="Page description" /> */}
      </Helmet>

      <PrivateComponent
        redirectOnError={{
          pathname: settings.settingsApp.loginPagePath || '/login/',
        }}
      >
        <BgWhite>
          <Grid>
            <Row justifyContent="center">
              <Col md={9}>
                <RexelBanners onWhite />
              </Col>
            </Row>
          </Grid>
        </BgWhite>
        <HomeBgGradient>
          <div className="spacer--top" />
          <IntroContainer>
            <Grid>
              <Row justifyContent="center">
                <Col xs={12} className="text-center">
                  <h1>
                    <StyledLogo
                      src={AdvantageLogo}
                      alt="Rexel Online Advantage"
                    />
                  </h1>
                  <p className="h2">
                    Welcome back to Rexel Online Advantage for 2026!
                    <b className="block">We are rewarding you with MORE!</b>
                  </p>
                  <hr />
                </Col>
              </Row>
              {/*<Row justifyContent="center">
              <Col xs={12} md={10} className="text-center">
                <MegaDrawContainer className="spacer--bottom--large">
                  <img src={megadraw} alt="" className="megadraw-logo" />
                  <div className="megadraw-text-container">
                    <h2>Introducing the MEGA CASH DRAW!</h2>
                    <p className="text--large">
                      WIN one of 4 prepaid visa cards valued at $5,000* each per
                      segment.
                    </p>
                    <div className="timeline-container">
                      <div className="line" />
                      <div className="timeline">
                        <div>
                          <div className="circle">1</div>
                          Mar - May
                        </div>
                        <div>
                          <div className="circle">2</div>
                          Jun - Aug
                        </div>
                        <div>
                          <div className="circle">3</div>
                          Sep - Nov
                        </div>
                      </div>
                    </div>
                    <p className="text--large">
                      For every eligible e-gift card earned, receive an entry
                      into the draw!
                    </p>
                    <p className="text--large">Terms and conditions apply.</p>
                  </div>
                </MegaDrawContainer>
              </Col>
            </Row>*/}
            </Grid>
          </IntroContainer>

          <InfoSection>
            <StatementStyles>
              <StatementProvider
                fetch={[
                  {
                    endpoint: '/Statement/v1/Rexel',
                    section: 'loyalty',
                  },
                ]}
                render={({ statement }) =>
                  !statement || !statement.data ? (
                    <LoadingAnimation /> // Loader instead of `null`?
                  ) : (
                    <CustomStatementSummary
                      fill="white"
                      statement={statement.data.loyalty}
                    />
                  )
                }
              />
              <p className="button--wrapper">
                <Link to="/my-activity">Find out more</Link>
              </p>
            </StatementStyles>

            <Grid className="how--it--works spacer--bottom">
              <Row justifyContent="center">
                <Col xs={10} className="text-center">
                  <h2 className="heading--how">How It Works</h2>
                </Col>
              </Row>
              <Row justifyContent="center">
                <Col
                  xs={10}
                  md={4}
                  className="text-center spacer--around--small"
                >
                  <IconWrapper height="150px">
                    <SvgIcon
                      icon="icon_giftcard_50"
                      scaleWidth="64"
                      scaleHeight="42"
                      width="100px"
                      height="65px"
                      fill="white"
                      title=""
                    />
                  </IconWrapper>
                  <b>
                    Spend $500 + gst on one order online on nominated products
                    from the featured vendor to qualify for a $50 e-gift card.
                  </b>
                </Col>
                <Col
                  xs={10}
                  md={4}
                  className="text-center spacer--around--small"
                >
                  <IconWrapper height="150px">
                    <SvgIcon
                      icon="icon-giftcard-4"
                      scaleWidth="64"
                      scaleHeight="42"
                      width="100px"
                      height="65px"
                      fill="white"
                      title=""
                    />
                  </IconWrapper>
                  <b>
                    Customers receive a e-gift card for the first 4 qualifying
                    ONLINE orders each promotional month only
                  </b>
                </Col>
              </Row>
            </Grid>
            <Grid className="promo--dates">
              <Row justifyContent="center">
                <Col xs={10} className="text-center">
                  <div>
                    <b>Promotion Starts:</b> <span>1 February 2026</span>
                  </div>

                  <div>
                    <b>Promotion Ends:</b> <span>30 November 2026</span>
                  </div>

                  <div>
                    <b>July 2026:</b> <span>Triple Treat. No eGift Cards.</span>
                  </div>
                </Col>
              </Row>
            </Grid>
          </InfoSection>
        </HomeBgGradient>
        <FeaturedSupplierContainer className="spacer--bottom">
          <Grid>
            <Row justifyContent="center">
              <Col xs={12} className="text-center">
                <h2 className="text--primary">Featured Vendors</h2>
              </Col>
            </Row>
            <Row justifyContent="center">
              <Col xs={11} sm={7} className="text-center divider">
                <img src={Clipsal} alt="Clipsal" />
                <p className="h3 text--secondary">
                  February, March, June, October, November
                </p>
              </Col>
            </Row>
            <Row justifyContent="center" className="spacer--top">
              <Col xs={11} sm={7} className="text-center divider">
                <img src={Atom} alt="Atom" />
                <p className="h3 text--secondary">April, September</p>
              </Col>
            </Row>
            <Row justifyContent="center" className="spacer">
              <Col xs={11} sm={7} className="text-center">
                <img src={Trader} alt="Trader" />
                <p className="h3 text--secondary">May, August</p>
              </Col>
            </Row>
          </Grid>
        </FeaturedSupplierContainer>
        <GiftCardContainer>
          <img src={cardsTop} alt="" className="card-top" />
          <img src={cardsBottom} alt="" className="card-bottom" />
          <Grid>
            <Row justifyContent="center">
              <Col xs={10} md={8} className="highlight text--center">
                Use your gift card dollars to select from our range of over
                <br />
                <span className="text--black">
                  50 digital e-gift cards
                </span>{' '}
                from leading brands
              </Col>
            </Row>
            <Row justifyContent="center">
              <Col xs={10} md={8} className="text--center">
                <p>
                  <strong>
                    You can claim each week when you receive your tracker email
                    or build up your dollars and claim multiple cards. Gift card
                    dollars can be claimed until 31st December 2026
                  </strong>
                </p>
              </Col>
            </Row>
          </Grid>
        </GiftCardContainer>

        <Grid>
          <Row justifyContent="center">
            <Col className="highlight text--center">
              <RewardsProvider
                showFeatured
                rewardLabels={{ highlightHeading: 'Rewards of the Month' }}
              />
            </Col>
          </Row>
        </Grid>
      </PrivateComponent>
    </PageContainer>
  );
};

HomePage.propTypes = {
  settings: PropTypes.object.isRequired,
};

export default withSettings(HomePage);
